// Seller product display: Shop Product Grid ("grid", the historical
// shopProductGrid behaviour) or Infinite Scroll ("infinite"), per device.
//
// Priority (resolveShopProductDisplayMode):
//   1. the Shop Builder element's own choice for this device,
//   2. the boutique's My Shop configuration (User.boutiques[].productDisplay),
//   3. the historical Shop Product Grid.
// Levels 1 and 2 only apply while the Developer allows Sellers to customize
// (AnimationSettings.infiniteScroll.sellerProductDisplay.enabled). Server
// rules: server_elakay/elakay_helpers/productDisplayRules.js.
import { useEffect, useState } from "react";
import { getInfiniteScrollViewportDevice } from "../../utils/infiniteScrollSettings";

export const PRODUCT_DISPLAY_MODES = ["grid", "infinite"];
export const PRODUCT_DISPLAY_DEVICES = ["desktop", "tablet", "mobile"];

export const DEFAULT_PRODUCT_DISPLAY = Object.freeze({
  customizationEnabled: false,
  defaultMode: "grid",
  devices: Object.freeze({ desktop: "grid", tablet: "grid", mobile: "grid" }),
});

const isMode = (value) => PRODUCT_DISPLAY_MODES.includes(value);

export const normalizeProductDisplay = (value) => {
  const source = value && typeof value === "object" ? value : {};
  const defaultMode = isMode(source.defaultMode) ? source.defaultMode : "grid";
  const devices = {};
  PRODUCT_DISPLAY_DEVICES.forEach((device) => {
    const mode = source.devices?.[device];
    devices[device] = isMode(mode) ? mode : (source.devices ? "grid" : defaultMode);
  });
  return { customizationEnabled: source.customizationEnabled === true, defaultMode, devices };
};

export const isSellerProductDisplayAllowed = (allSettings) => allSettings?.infiniteScroll?.sellerProductDisplay?.enabled === true;

// Builder element option: { deviceModes: { desktop|tablet|mobile: "inherit"|"grid"|"infinite" } }.
export const normalizeBlockDeviceModes = (value) => {
  const out = {};
  PRODUCT_DISPLAY_DEVICES.forEach((device) => {
    const mode = value?.[device];
    out[device] = isMode(mode) ? mode : "inherit";
  });
  return out;
};

export const resolveShopProductDisplayMode = ({ allSettings, shopDisplay, blockDeviceModes = null, device }) => {
  if (!isSellerProductDisplayAllowed(allSettings)) return "grid";
  const blockMode = normalizeBlockDeviceModes(blockDeviceModes)[device];
  if (isMode(blockMode)) return blockMode;
  const display = normalizeProductDisplay(shopDisplay);
  if (display.customizationEnabled) return display.devices[device] || display.defaultMode;
  return "grid";
};

// Same breakpoints as the existing infinite scroll settings (≤767 mobile,
// ≤1024 tablet); re-evaluated on resize / orientation change.
export const useViewportDevice = () => {
  const [device, setDevice] = useState(() => getInfiniteScrollViewportDevice());
  useEffect(() => {
    const update = () => setDevice(getInfiniteScrollViewportDevice());
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);
  return device;
};

// Loading mode of a Shop Builder "Grille Produits" element (ProductGridShop
// `loadingMode`): "inherit" = historical Shop Product Grid behaviour,
// "pagination" / "infinite" = explicit. Same priority as above; the
// element's older single `loadingMode` option counts as an explicit choice
// for every device that has no device-specific choice.
export const resolveBlockLoadingMode = ({ allSettings, shopDisplay, options = {}, device }) => {
  if (!isSellerProductDisplayAllowed(allSettings)) return "inherit";
  const deviceMode = normalizeBlockDeviceModes(options.deviceModes)[device];
  if (deviceMode === "infinite") return "infinite";
  if (deviceMode === "grid") return "inherit";
  if (options.loadingMode === "infinite" || options.loadingMode === "pagination") return options.loadingMode;
  return resolveShopProductDisplayMode({ allSettings, shopDisplay, device }) === "infinite" ? "infinite" : "inherit";
};
